import { useEffect, useMemo } from 'react';

// Custom hook to use the intersection observer api,
// to observe changes in the position of an **UNIQUE** element
// Will trigger the callback with the boundingClientRect of the element
// the intersection ratio, the intersection rect, if it is intersecting
// and the html element itself, so you can query for extra information
export const useNativeIntersectionObserver = <T extends HTMLElement>(
  el: T | null,
  callback: (entry: Omit<IntersectionObserverEntry, 'target'> & { target: T }) => void,
) => {
  const intersectionObserver = useMemo(
    () =>
      new IntersectionObserver((entries) => {
        const entry = entries[0]; // We will only observe one element

        const target = entry.target as T;

        callback({
          boundingClientRect: entry.boundingClientRect,
          intersectionRatio: entry.intersectionRatio,
          intersectionRect: entry.intersectionRect,
          isIntersecting: entry.isIntersecting,
          rootBounds: entry.rootBounds,
          target,
          time: entry.time,
        });
      }),
    [callback],
  );

  useEffect(() => {
    if (el) intersectionObserver.observe(el);
    return () => intersectionObserver.disconnect();
  }, [el, intersectionObserver]);
};
